import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  ProductBtnLabels,
  ProductDescLabels,
  ProductImagesLabels,
  ProductPricingLabels,
  ProductTitleLabels,
  ProductTags,
  Route,
} from '../../../enums';
import { Region, RenewalSubscriptionStatus } from '@hiwaldo/sdk';
import { usePageEnvironmentEffect } from '../../../hooks/common/environment/environment.hook';
import { SiteFeatureList } from '../../../server/static/utils/features.static-util';
import { ExtendedRouteItem } from '../../../hooks/common/routing/routing.hook';

interface ProductRouteItem extends ExtendedRouteItem {
  title: string;
  titleExt?: string;
  link: string;
  summary: string;
  priceExt: React.ReactNode;
  btnLabel: string;
  btnLink: string;
  imageHover: string;
  itsNew?: boolean;
}

export const homeOurProducts = (
  region: Region,
  customerSubscriptionStatus: RenewalSubscriptionStatus
): any[] => {
  const regionalOurProducts: any = [];
  let dailyLensPrice = ProductPricingLabels.DAILY_LENS_US;
  let vitaminLensPrice;
  let btnLabelScenario = 'GET FREE TRIAL';

  switch (region) {
    case Region.UK: {
      dailyLensPrice = ProductPricingLabels.DAILY_LENS_UK;
      vitaminLensPrice = ProductPricingLabels.VITAMIN_UK;
      break;
    }
    case Region.EU: {
      dailyLensPrice = ProductPricingLabels.DAILY_LENS_EU;
      vitaminLensPrice = ProductPricingLabels.VITAMIN_EU;
      break;
    }
    case Region.SE: {
      dailyLensPrice = ProductPricingLabels.DAILY_LENS_SE;
      vitaminLensPrice = ProductPricingLabels.VITAMIN_SE;
      break;
    }
  }

  if (customerSubscriptionStatus === RenewalSubscriptionStatus.ACTIVE) {
    btnLabelScenario = ProductBtnLabels.DAILY_LENS_ACTIVE;
  }

  if (region === Region.US) {
    regionalOurProducts.push({
      title: ProductTitleLabels.HYDRABOOST_PLUS,
      titleExt: ProductTitleLabels.HYDRABOOST_PLUS_EXT,
      link: Route.PRODUCTS_HYDRABOOST_PLUS,
      summary: ProductDescLabels.HYDRABOOST_PLUS,
      pricing: (
        <>
          <span className="font-medium">
            {ProductPricingLabels.HYDRABOOST_PLUS}
          </span>{' '}
          per box
        </>
      ),
      btnLabel: ProductBtnLabels.HYDRABOOST_PLUS,
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.HYDRABOOST_PLUS,
      imageHover: ProductImagesLabels.HYDRABOOST_PLUS,
      tag: ProductTags.SUNGLASSES_PROMO,
      promotion: true,
    });
    regionalOurProducts.push({
      title: ProductTitleLabels.DAILY_LENS,
      link: Route.PRODUCTS_ORIGINAL,
      summary: ProductDescLabels.DAILY_LENS,
      pricing: (
        <>
          <span className="font-medium">{dailyLensPrice}</span> per box
        </>
      ),
      btnLabel: btnLabelScenario,
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.DAILY_LENS,
      imageHover: ProductImagesLabels.DAILY_LENS_HOVER,
      promotion: false,
    });
  }

  regionalOurProducts.push({
    title: ProductTitleLabels.STARTER_KIT,
    link: Route.PRODUCTS_EYE_STARTER_KIT,
    summary: ProductDescLabels.STARTER_KIT,
    pricing: (
      <>
        <span className="font-medium">{ProductPricingLabels.STARTER_KIT}</span>
        <span className="ml-0.5 line-through text-opacity-50">$30</span>
      </>
    ),
    btnLabel: ProductBtnLabels.STARTER_KIT,
    btnLink: Route.GET_STARTED,
    image: ProductImagesLabels.STARTER_KIT,
    imageHover: ProductImagesLabels.STARTER_KIT_HOVER,
    promotion: false,
  });

  if (region !== Region.US) {
    regionalOurProducts.push({
      title: ProductTitleLabels.VITAMIN,
      link: Route.PRODUCTS_VITAMIN,
      summary: ProductDescLabels.VITAMIN,
      pricing: (
        <>
          <span className="font-medium">{vitaminLensPrice}</span> per box
        </>
      ),
      btnLabel: ProductBtnLabels.VITAMIN,
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.VITAMIN,
      imageHover: ProductImagesLabels.VITAMIN,
      promotion: false,
    });
  }

  if (region === Region.US) {
    regionalOurProducts.push({
      title: ProductTitleLabels.DROPS,
      link: Route.PRODUCT_HYDRATION_DROPS,
      summary: ProductDescLabels.DROPS,
      pricing: (
        <>
          <span className="font-medium">{ProductPricingLabels.DROPS}</span>
        </>
      ),
      btnLabel: ProductBtnLabels.DROPS,
      btnLink: Route.PRODUCT_HYDRATION_DROPS,
      image: ProductImagesLabels.DROPS,
      imageHover: ProductImagesLabels.DROPS,
    });
  }

  return regionalOurProducts;
};

export const shopOurProducts = {
  OUR_PRODUCTS_US: [
    {
      title: ProductTitleLabels.HYDRABOOST_PLUS,
      titleExt: ProductTitleLabels.HYDRABOOST_PLUS_EXT,
      link: Route.PRODUCTS_HYDRABOOST_PLUS,
      summary: ProductDescLabels.HYDRABOOST_PLUS,
      pricing: (
        <>
          <span className="font-medium">
            {ProductPricingLabels.HYDRABOOST_PLUS}
          </span>{' '}
          per box
        </>
      ),
      btnLabel: ProductBtnLabels.HYDRABOOST_PLUS,
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.HYDRABOOST_PLUS,
      imageHover: ProductImagesLabels.HYDRABOOST_PLUS,
      tag: ProductTags.SUNGLASSES_PROMO,
      promotion: true,
      active: true,
    },
    {
      title: ProductTitleLabels.DAILY_LENS,
      titleExt: false,
      link: Route.PRODUCTS_ORIGINAL,
      summary: ProductDescLabels.DAILY_LENS,
      pricing: (
        <>
          <span className="font-medium">$20</span> per box
        </>
      ),
      btnLabel: 'GET FREE TRIAL',
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.DAILY_LENS,
      imageHover: ProductImagesLabels.DAILY_LENS_HOVER,
      tag: ProductTags.SUNGLASSES_PROMO,
      promotion: true,
      active: true,
    },
    {
      title: ProductTitleLabels.STARTER_KIT,
      titleExt: false,
      link: Route.PRODUCTS_EYE_STARTER_KIT,
      summary: ProductDescLabels.STARTER_KIT,
      pricing: (
        <>
          <span className="font-medium">$7</span>
          <span className="ml-0.5 line-through text-opacity-50">$30</span>
        </>
      ),
      btnLabel: ProductBtnLabels.STARTER_KIT,
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.STARTER_KIT,
      imageHover: ProductImagesLabels.STARTER_KIT_HOVER,
      tag: ProductTags.SUNGLASSES_PROMO,
      promotion: true,
      active: false,
    },
    {
      title: ProductTitleLabels.DROPS,
      titleExt: false,
      link: Route.PRODUCT_HYDRATION_DROPS,
      summary: ProductDescLabels.DROPS,
      pricing: (
        <>
          <span className="font-medium">{ProductPricingLabels.DROPS}</span>
        </>
      ),
      btnLabel: ProductBtnLabels.DROPS,
      btnLink: Route.PRODUCT_HYDRATION_DROPS,
      image: ProductImagesLabels.DROPS,
      imageHover: ProductImagesLabels.DROPS,
      tag: '',
      promotion: false,
      active: false,
    },
  ],
  OUR_PRODUCTS_UK: [
    {
      title: ProductTitleLabels.DAILY_LENS,
      titleExt: false,
      link: Route.PRODUCTS_ORIGINAL,
      summary: ProductDescLabels.DAILY_LENS,
      pricing: (
        <>
          <span className="font-medium">£12</span> per box
        </>
      ),
      btnLabel: 'GET FREE TRIAL',
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.DAILY_LENS,
      imageHover: ProductImagesLabels.DAILY_LENS_HOVER,
      tag: ProductTags.HYDRABOOST_PLUS_TAG,
      promotion: false,
      active: true,
    },
    {
      title: ProductTitleLabels.VITAMIN,
      titleExt: false,
      link: Route.PRODUCTS_VITAMIN,
      summary: ProductDescLabels.VITAMIN,
      pricing: (
        <>
          <span className="font-medium">£16</span> per box
        </>
      ),
      btnLabel: ProductBtnLabels.VITAMIN,
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.VITAMIN,
      imageHover: ProductImagesLabels.VITAMIN,
      tag: ProductTags.HYDRABOOST_PLUS_TAG,
      promotion: false,
      active: true,
    },
  ],
  OUR_PRODUCTS_EU: [
    {
      title: ProductTitleLabels.DAILY_LENS,
      titleExt: false,
      link: Route.PRODUCTS_ORIGINAL,
      summary: ProductDescLabels.DAILY_LENS,
      pricing: (
        <>
          <span className="font-medium">16 €</span> per box
        </>
      ),
      btnLabel: 'GET FREE TRIAL',
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.DAILY_LENS,
      imageHover: ProductImagesLabels.DAILY_LENS_HOVER,
      tag: '',
      promotion: false,
      active: true,
    },
    {
      title: ProductTitleLabels.VITAMIN,
      titleExt: false,
      link: Route.PRODUCTS_VITAMIN,
      summary: ProductDescLabels.VITAMIN,
      pricing: (
        <>
          <span className="font-medium">19 €</span> per box
        </>
      ),
      btnLabel: ProductBtnLabels.VITAMIN,
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.VITAMIN,
      imageHover: ProductImagesLabels.VITAMIN,
      tag: '',
      promotion: false,
      active: true,
    },
  ],
  OUR_PRODUCTS_SE: [
    {
      title: ProductTitleLabels.DAILY_LENS,
      titleExt: false,
      link: Route.PRODUCTS_ORIGINAL,
      summary: ProductDescLabels.DAILY_LENS,
      pricing: (
        <>
          <span className="font-medium">150 kr</span> per box
        </>
      ),
      btnLabel: 'GET FREE TRIAL',
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.DAILY_LENS,
      imageHover: ProductImagesLabels.DAILY_LENS_HOVER,
      tag: '',
      promotion: false,
      active: true,
    },
    {
      title: ProductTitleLabels.VITAMIN,
      titleExt: false,
      link: Route.PRODUCTS_VITAMIN,
      summary: ProductDescLabels.VITAMIN,
      pricing: (
        <>
          <span className="font-medium">200 kr</span> per box
        </>
      ),
      btnLabel: ProductBtnLabels.VITAMIN,
      btnLink: Route.GET_STARTED,
      image: ProductImagesLabels.VITAMIN,
      imageHover: ProductImagesLabels.VITAMIN,
      tag: '',
      promotion: false,
      active: true,
    },
  ],
};

interface ProductCardProps {
  activeFeatures: SiteFeatureList;
}

export const OurProducts = ({ activeFeatures }: ProductCardProps) => {
  const { region } = usePageEnvironmentEffect();
  const router = useRouter();
  const vitLensesPrice =
    region === 'UK'
      ? ProductPricingLabels.VITAMIN_UK
      : region === 'EU'
      ? ProductPricingLabels.VITAMIN_EU
      : region === 'SE'
      ? ProductPricingLabels.VITAMIN_SE
      : '';
  const hbPlusPrice = ProductPricingLabels.HYDRABOOST_PLUS;

  const productDetails: ProductRouteItem[] = [
    {
      handle: 'hydraboost-plus',
      title: 'Daily Contact Lenses',
      titleExt: 'with vitamin B12',
      link: Route.PRODUCTS_HYDRABOOST_PLUS,
      summary: 'An extra hydration boost',
      alt: 'Daily Contact Lenses',
      priceExt: <span className="font-medium">{hbPlusPrice}</span>,
      btnLabel: ProductBtnLabels.HYDRABOOST_PLUS,
      btnLink: Route.PRODUCTS_HYDRABOOST_PLUS,
      image: '/images/home/products/hydraboost-plus.png',
      imageHover: '/images/home/products/hydraboost-plus.png',
      itsNew: true,
      onLinkHandler: () => {
        router.push(Route.PRODUCTS_HYDRABOOST_PLUS);
      },
      label: 'hydraboost-plus',
    },
    {
      handle: 'hydraboost',
      title: ProductTitleLabels.DAILY_LENS,
      link: Route.PRODUCTS_ORIGINAL,
      summary: ProductDescLabels.DAILY_LENS,
      alt: 'Daily Contact Lenses',
      priceExt: (
        <span className="font-medium">
          {region === 'US'
            ? ProductPricingLabels.DAILY_LENS_US
            : ProductPricingLabels.DAILY_LENS_UK}
        </span>
      ),
      btnLabel: ProductBtnLabels.DAILY_LENS,
      btnLink: Route.PRODUCTS_ORIGINAL,
      image: ProductImagesLabels.DAILY_LENS,
      imageHover: ProductImagesLabels.DAILY_LENS_HOVER,
      onLinkHandler: () => {
        router.push(Route.PRODUCTS_ORIGINAL);
      },
      label: 'hydraboost',
    },
    {
      handle: 'vitamin',
      title: ProductTitleLabels.VITAMIN,
      link: Route.PRODUCTS_VITAMIN,
      summary: ProductDescLabels.VITAMIN,
      alt: 'Vitamin Daily Contact Lenses',
      priceExt: vitLensesPrice ? (
        <span className="font-medium">{vitLensesPrice}</span>
      ) : null,
      btnLabel: ProductBtnLabels.VITAMIN,
      btnLink: Route.PRODUCTS_VITAMIN,
      image: ProductImagesLabels.VITAMIN,
      imageHover: ProductImagesLabels.VITAMIN_HOVER,
      onLinkHandler: () => {
        router.push(Route.PRODUCTS_VITAMIN);
      },
      label: 'vitamin',
    },
    {
      handle: 'drops',
      title: ProductTitleLabels.DROPS,
      link: Route.PRODUCT_HYDRATION_DROPS,
      summary: ProductDescLabels.DROPS,
      alt: 'Eye Drops',
      priceExt: (
        <span className="font-medium">{ProductPricingLabels.DROPS}</span>
      ),
      btnLabel: ProductBtnLabels.DROPS,
      btnLink: Route.PRODUCT_HYDRATION_DROPS,
      image: ProductImagesLabels.DROPS,
      imageHover: ProductImagesLabels.DROPS_HOVER,
      onLinkHandler: () => {
        router.push(Route.PRODUCT_HYDRATION_DROPS);
      },
      label: 'drops',
    },
  ];

  const desktopProducts = () => {
    const hideVitaminLenses = region === 'US';
    const hideDrops = region !== 'US';

    return productDetails
      .filter(
        (p) =>
          !(
            (p.handle === 'vitamin' && hideVitaminLenses) ||
            (p.handle === 'drops' && hideDrops)
          )
      )
      .map((item, index) => (
        <div key={index} className="flex-col">
          <Link
            href={item.link}
            title={item.title}
            className="w-full flex h-[250px] rounded-[10px] overflow-hidden transform transition duration-500 ease-in-out hover:-translate-y-1 hover:shadow-lg relative"
          >
            {item.itsNew && (
              <div className="absolute top-0 left-0 z-20 w-[84px] h-[24px] m-2 rounded bg-signal-success flex items-center justify-center">
                <p className="text-white text-xs font-semibold uppercase">
                  New
                </p>
              </div>
            )}
            <div className="absolute inset-0 bg-gray-50 opacity-20 z-0"></div>
            <Image
              src={item.image}
              alt={item.alt}
              width={504}
              height={408}
              className="h-auto w-full object-contain"
            />
          </Link>
          <div className="flex-grow pt-[30px] min-h-[152px]">
            <div className="pb-4 text-2xl font-medium text-secondary-900 leading-normal">
              {item.title}
              {item.titleExt && (
                <span className="block text-sm font-medium text-primary-500">
                  {item.titleExt}
                </span>
              )}
            </div>
            <p className="pb-4 text-sm text-secondary-700">{item.summary}</p>
            {item.priceExt}
          </div>
          <div className="w-full">
            <Link
              href={item.btnLink}
              title={item.btnLabel}
              className="flex justify-center w-full p-3 text-sm font-semibold tracking-wide text-white uppercase bg-primary-500 rounded"
            >
              {item.btnLabel}
            </Link>
          </div>
        </div>
      ));
  };

  return (
    <div className="mx-auto my-5">
      <div className="px-4 mx-auto max-w-screen-xl mb-8 lg:mb-16">
        <h2 className="text-3xl font-semibold text-secondary-900 mb-8 md:mb-16 text-center">
          Our Products
        </h2>
        <div className="grid max-w-screen-xl grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3 xl:gap-16">
          {desktopProducts()}
        </div>
      </div>
    </div>
  );
};
